import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ProductCardActions } from '../../../Catalog'
import { useProductSliderVariants } from '../../../Theme'
import { ProductCard, SectionTitle } from '../../../UiBlocks'
import { useProductSliderData } from '../../hooks'
import { LABELS } from '../../lib'
import { NavigationButton } from '../shared'
import { ProductSliderSkeleton } from './ProductSliderSkeleton'

import type { HomepageProductSlider, Product } from '../../types'

// Swiper styles are imported globally in styles/globals.css

/** Action row (cart or details, wishlist) for ProductCard, shared by the grid and carousel layouts. */
const renderActions = (product: Product) => <ProductCardActions product={product} />

interface ProductSliderProps {
  slider: HomepageProductSlider
  /** Global loading state (config loading) - shows skeleton before query starts */
  isLoading?: boolean
}

/**
 * ProductSlider - Carousel or static grid of products
 *
 * Layout switches by `slider.display_type`:
 * - 'slider' → Swiper carousel (1 / 2 / 3 responsive)
 * - 'grid'   → Static CSS grid (2 mobile / 3 desktop)
 *
 * Fetches products based on slider config (source_type + source_value):
 * - source_type="category" → fetches by category ID
 * - source_type="skus" → fetches by SKU list
 */
export function ProductSlider({ slider, isLoading: configLoading = false }: ProductSliderProps) {
  const {
    productSliderSectionVariants,
    productSliderWrapperVariants,
    productSliderSwiperVariants,
    productSliderGridVariants,
    productSliderGridItemVariants,
  } = useProductSliderVariants()

  const { products, isLoading: productsLoading } = useProductSliderData({ slider })

  if (configLoading || productsLoading) {
    return <ProductSliderSkeleton />
  }

  if (products.length === 0) {
    return null
  }

  const isGrid = slider.display_type === 'grid'

  return (
    <section className={productSliderSectionVariants()}>
      <SectionTitle size="lg">{slider.title}</SectionTitle>

      <div className={productSliderWrapperVariants()}>
        {isGrid ? (
          <div className={productSliderGridVariants()}>
            {products.map((product) => (
              <div key={product.sku} className={productSliderGridItemVariants()}>
                <ProductCard
                  product={product}
                  className="h-full"
                  priceSuffix={LABELS.PRICE_SUFFIX}
                  renderActions={renderActions}
                />
              </div>
            ))}
          </div>
        ) : (
          <SliderCarousel slider={slider} products={products} swiperClass={productSliderSwiperVariants()} />
        )}
      </div>
    </section>
  )
}

interface SliderCarouselProps {
  slider: HomepageProductSlider
  products: Product[]
  swiperClass: string
}

function SliderCarousel({ slider, products, swiperClass }: SliderCarouselProps) {
  const { productSliderSlideVariants } = useProductSliderVariants()

  const navSelectors = {
    prevEl: `.product-swiper-prev-${slider.id}`,
    nextEl: `.product-swiper-next-${slider.id}`,
    prevClass: `product-swiper-prev-${slider.id}`,
    nextClass: `product-swiper-next-${slider.id}`,
  }

  const enableLoop = products.length > 3

  return (
    <Swiper
      modules={[Navigation]}
      spaceBetween={16}
      slidesPerView={1}
      loop={enableLoop}
      navigation={{
        prevEl: navSelectors.prevEl,
        nextEl: navSelectors.nextEl,
      }}
      breakpoints={{
        320: { slidesPerView: 1, spaceBetween: 8 },
        640: { slidesPerView: 2, spaceBetween: 12 },
        1024: { slidesPerView: 3, spaceBetween: 16 },
      }}
      className={swiperClass}
    >
      {products.map((product) => (
        <SwiperSlide key={product.sku} className={productSliderSlideVariants()}>
          <ProductCard
            product={product}
            className="h-full"
            priceSuffix={LABELS.PRICE_SUFFIX}
            renderActions={renderActions}
          />
        </SwiperSlide>
      ))}

      <NavigationButton direction="prev" size="product" className={navSelectors.prevClass} />
      <NavigationButton direction="next" size="product" className={navSelectors.nextClass} />
    </Swiper>
  )
}
